/* Created by Interactive 63 - https://interactive63.com */
/**
 * Hand-made correspondence for shape morphs. A pin says that a point of the previous shape turns
 * into a point of this one; parts say which part of the previous shape each part of this one
 * grows from. Pinned points meet exactly, and the segments between pins are divided evenly on
 * both sides, so the morph between them stays untwisted. Anything unpinned is matched
 * automatically, as before.
 */
import type { Alignment, MatchOptions } from './match';
import { alignFrame, equaliseSegments, matchSubpaths, rotateSubpath, subdivide } from './match';
import type { Cubic, Subpath } from './normalise';

/** A pin: the index of a point in the previous shape and of the point it becomes in this one. */
export type Pin = readonly [from: number, to: number];

/** Pins and parts for one shape, relative to the shape before it. */
export interface FrameExtras {
  pins?: readonly Pin[];
  /** For each part of this shape, in size order, the part of the previous shape it comes from. */
  parts?: readonly (number | null)[];
}

/** How many points a subpath has: one per segment start, plus the end of an open one. */
export function anchorCount(path: Subpath): number {
  return path.closed ? path.segs.length : path.segs.length + 1;
}

/** The point at which a segment starts. */
function startOf(path: Subpath, segment: number): [number, number] {
  if (segment === 0) return path.start;
  const previous = path.segs[segment - 1] as Cubic;
  return [previous[4], previous[5]];
}

/** The points of a subpath that pins refer to, in order. */
export function anchorPoints(path: Subpath): [number, number][] {
  return Array.from({ length: anchorCount(path) }, (_, i) => startOf(path, i));
}

/** Converts a point index counted across every part into a part and a point within it. */
export function locateAnchor(
  frame: readonly Subpath[],
  index: number,
): { part: number; point: number } | null {
  let offset = 0;
  for (const [part, path] of frame.entries()) {
    const count = anchorCount(path);
    if (index < offset + count) return { part, point: index - offset };
    offset += count;
  }
  return null;
}

/**
 * Lines up one pair of subpaths at their pins. Both are turned to start at the first pin, then
 * every stretch between neighbouring pins gets the same number of segments on both sides.
 * Returns the reason when the pins cannot be honoured.
 */
export function pinnedPair(
  a: Subpath,
  b: Subpath,
  pins: readonly Pin[],
): { a: Subpath; b: Subpath } | string {
  if (a.closed !== b.closed) return 'Pins need both shapes to be closed, or both open.';
  const na = anchorCount(a);
  const nb = anchorCount(b);
  const unique = [
    ...new Map(
      pins.filter(([x, y]) => x >= 0 && x < na && y >= 0 && y < nb).map((p) => [p[0], p]),
    ).values(),
  ].sort((p, q) => p[0] - q[0]);
  const first = unique[0];
  if (!first) return 'There are no pins on these parts.';

  let ra = a;
  let rb = b;
  let relative: [number, number][];
  if (a.closed) {
    ra = rotateSubpath(a, first[0]);
    rb = rotateSubpath(b, first[1]);
    relative = unique.map(([x, y]) => [(x - first[0] + na) % na, (y - first[1] + nb) % nb]);
  } else {
    relative = unique.map(([x, y]) => [x, y]);
  }
  for (let i = 1; i < relative.length; i += 1) {
    const here = relative[i] as [number, number];
    const before = relative[i - 1] as [number, number];
    if (here[1] <= before[1])
      return 'Pins cross: each pin must follow the one before it around both shapes.';
  }

  // Stretch boundaries in segment indices on each side.
  const ends = (side: 0 | 1, path: Subpath): number[] => {
    const marks = relative.map((pair) => pair[side]);
    const all = a.closed ? [...marks, path.segs.length] : [0, ...marks, path.segs.length];
    return [...new Set(all)].sort((p, q) => p - q);
  };
  const boundsA = ends(0, ra);
  const boundsB = ends(1, rb);
  if (boundsA.length !== boundsB.length) return 'Pins sit too close to the ends of the path.';

  const segsA: Cubic[] = [];
  const segsB: Cubic[] = [];
  for (let i = 0; i + 1 < boundsA.length; i += 1) {
    const sliceA = ra.segs.slice(boundsA[i], boundsA[i + 1]);
    const sliceB = rb.segs.slice(boundsB[i], boundsB[i + 1]);
    if (sliceA.length === 0 || sliceB.length === 0) return 'Two pins share a point on one shape.';
    const target = Math.max(sliceA.length, sliceB.length);
    const stretch = (path: Subpath, from: number, segs: Cubic[]): Cubic[] =>
      subdivide({ start: startOf(path, from), segs, closed: false }, target).segs;
    segsA.push(...stretch(ra, boundsA[i] ?? 0, sliceA));
    segsB.push(...stretch(rb, boundsB[i] ?? 0, sliceB));
  }
  return { a: { ...ra, segs: segsA }, b: { ...rb, segs: segsB } };
}

/**
 * Reorders the parts of a shape so each sits beside the part of the previous shape it was
 * paired with. Unpaired parts fill the remaining places in their own order. Returns the new
 * order and where each original part went.
 */
export function orderParts(
  frame: readonly Subpath[],
  parts: readonly (number | null)[],
): { frame: Subpath[]; slotOf: number[] } {
  const slots: (Subpath | undefined)[] = Array.from({ length: frame.length });
  const slotOf = Array.from({ length: frame.length }, () => -1);
  frame.forEach((path, i) => {
    const wanted = parts[i];
    if (
      wanted !== null &&
      wanted !== undefined &&
      wanted >= 0 &&
      wanted < frame.length &&
      !slots[wanted]
    ) {
      slots[wanted] = path;
      slotOf[i] = wanted;
    }
  });
  frame.forEach((path, i) => {
    if (slotOf[i] !== -1) return;
    const free = slots.findIndex((slot) => slot === undefined);
    slots[free] = path;
    slotOf[i] = free;
  });
  return { frame: slots as Subpath[], slotOf };
}

/**
 * Makes frames morph-compatible like `prepareFrames`, honouring pins and parts. Parts reorder a
 * shape before anything else; pinned pairs are lined up before the segment counts are evened
 * out; and pinned parts keep the start point their pins gave them instead of being turned to the
 * closest fit. Pins that cannot be honoured are ignored and reported.
 */
export function preparePinnedFrames(
  frames: readonly (readonly Subpath[])[],
  options: MatchOptions,
  manual: readonly (Alignment | undefined)[],
  extras: readonly (FrameExtras | undefined)[],
): { frames: Subpath[][]; problems: string[] } {
  const problems: string[] = [];
  const matched = matchSubpaths(frames);
  const slotOfs: number[][] = [];
  const ordered = matched.map((frame, k) => {
    const parts = k > 0 ? extras[k]?.parts : undefined;
    if (!parts) {
      slotOfs.push(frame.map((_, i) => i));
      return [...frame];
    }
    const result = orderParts(frame, parts);
    slotOfs.push(result.slotOf);
    return result.frame;
  });

  // Parts of each frame whose start point was fixed by pins, so automatic turning leaves them.
  const fixed = ordered.map(() => new Set<number>());
  ordered.forEach((frame, k) => {
    const pins = extras[k]?.pins;
    const previous = ordered[k - 1];
    if (k === 0 || !pins || pins.length === 0 || !previous) return;
    const byPart = new Map<number, Pin[]>();
    for (const [from, to] of pins) {
      const a = locateAnchor(matched[k - 1] ?? [], from);
      const b = locateAnchor(matched[k] ?? [], to);
      if (!a || !b) {
        problems.push('A pin points at a part of the shape that has changed.');
        continue;
      }
      const slotA = slotOfs[k - 1]?.[a.part] ?? a.part;
      const slotB = slotOfs[k]?.[b.part] ?? b.part;
      if (slotA !== slotB) {
        problems.push('A pin joins two parts that are not paired with each other.');
        continue;
      }
      byPart.set(slotA, [...(byPart.get(slotA) ?? []), [a.point, b.point]]);
    }
    for (const [slot, list] of byPart) {
      const pathA = previous[slot];
      const pathB = frame[slot];
      if (!pathA || !pathB) continue;
      const result = pinnedPair(pathA, pathB, list);
      if (typeof result === 'string') {
        problems.push(result);
        continue;
      }
      previous[slot] = result.a;
      frame[slot] = result.b;
      fixed[k - 1]?.add(slot);
      fixed[k]?.add(slot);
    }
  });

  const equal = equaliseSegments(ordered);
  const result: Subpath[][] = [];
  equal.forEach((frame, index) => {
    const before = result[index - 1];
    if (!before) {
      result.push(frame);
      return;
    }
    const aligned = alignFrame(before, frame, options, manual[index]);
    result.push(aligned.map((path, i) => (fixed[index]?.has(i) ? (frame[i] as Subpath) : path)));
  });
  return { frames: result, problems };
}
